import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Glyphicon } from '@bingads-webui-react/primitive';

import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { getStyles } from './get-styles';
import { LocationTarget } from '../util/index';

/**
 * Displays the description of a location target and optionally any errors or additional UI.
 * The name is misleading, it's not the entire grid row but only the text without any icon buttons.
 * This component is exported by the package so that others can re-use the formatting and styling.
 * @param {*} props - React props.
 * @returns {ReactNode} - React component.
 */
function LocationTargetsGridRowUnstyled(props) {
  const {
    children,
    classes,
    isNegativeLocationTarget,
    locationTarget,
  } = props;
  const i18n = useContext(I18nContext);

  const gridRowText = useMemo(() => locationTarget.getGridRowText(i18n), [i18n, locationTarget]);

  const pushpin = useMemo(() => {
    if (!isNegativeLocationTarget) {
      return (
        <Glyphicon
          glyph="ba-POILocationFoundSolid"
          className="location-icon"
          title={i18n.getString(_TL_('Targeted'))}
        />
      );
    }

    return (
      <Glyphicon
        glyph="ba-POILocationNotFoundSolid"
        className="negative-location-icon"
        title={i18n.getString(_TL_('Excluded'))}
      />
    );
  }, [isNegativeLocationTarget, i18n]);

  const a11yProps = isNegativeLocationTarget
    ? {}
    : {
      'aria-label': i18n.getString(_TL_('Targeted location: {{location}}'), { location: gridRowText.locationDescription }),
      tabIndex: '0',
      role: 'textbox',
    };

  return (
    <div
      className={classNames('location-target-grid-row', classes.locationTargetGridRow)}
      {...a11yProps}
    >
      {pushpin}
      <strong className="targeting-description-text">{gridRowText.radiusDescription}</strong>
      {' '}
      <span className="location-target-name">{gridRowText.locationDescription}</span>

      {children &&
        <React.Fragment>
          {' '}
          {children}
        </React.Fragment>
      }
    </div>
  );
}

LocationTargetsGridRowUnstyled.propTypes = {
  classes: PropTypes.shape({
    locationTargetGridRow: PropTypes.string,
  }).isRequired,

  /**
   * Optionally display any children at the end of this grid row.
   */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),

  locationTarget: PropTypes.instanceOf(LocationTarget).isRequired,
  isNegativeLocationTarget: PropTypes.bool,
};

LocationTargetsGridRowUnstyled.defaultProps = {
  children: null,
  isNegativeLocationTarget: false,
};

export const LocationTargetsGridRow = withDefaultStyles(LocationTargetsGridRowUnstyled, getStyles);
